import { useState, useEffect, useCallback, useMemo } from "react";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { useRouter } from "expo-router";
import { useUser, useTenantId } from "@src/features/tenants";
import { useCameras } from "@src/features/cameras";
import { resolveCameraDisplayName } from "@src/features/cameras/utils/camera-display-name";
import { useActivityNotifications, type ActivityNotificationItem } from "@visioni/a3s-activity/app";
import type { ActivityItem, ActivityStep, ActivityStepStatus } from "@visioni/a3s-activity/app";
import { hasUnreadNotifications, isNotificationRead } from "@visioni/a3s-activity/app";

const LAST_SEEN_PREFIX = "@notifications_last_seen";
const STALE_THRESHOLD_MS = 30 * 60 * 1000;

export interface UseNotificationPanelResult {
  visible: boolean;
  hasUnread: boolean;
  activities: ActivityItem[];
  hasMore: boolean;
  isLoadingMore: boolean;
  onOpen: () => void;
  onClose: () => void;
  onMarkAllRead: () => void;
  onNavigate: (activity: ActivityItem) => void;
  onLoadMore: () => void;
}

function edgeStateLabel(state: string): string {
  switch (state) {
    case "uploading":
      return "Change requested";
    case "applying":
      return "Applying to camera";
    case "applied":
      return "Applied successfully";
    case "failed":
    default:
      return "Failed";
  }
}

const NOTIFICATION_KINDS = [
  "alarm_activated",
  "alarm_routine_configured",
  "alarm_response_configured",
  "alarm_exception_configured",
  "alarm_timezone_configured",
  "alarm_override_configured",
  "alarm_silence_configured",
  "alarm_contacts_configured",
  "deterrence_triggered",
  "deterrence_stopped",
];

const CONFIGURATION_KINDS = new Set([
  "alarm_routine_configured",
  "alarm_response_configured",
  "alarm_exception_configured",
  "alarm_timezone_configured",
  "alarm_silence_configured",
  "alarm_override_configured",
  "alarm_contacts_configured",
]);

function deriveStatus(item: ActivityNotificationItem): ActivityStepStatus | undefined {
  if (item.edge == null) return undefined;
  const { status, events } = item.edge;
  if (status === "successful") return "completed";
  if (status === "failed") return "failed";
  if (status === "pending" && events.length > 0) {
    const lastEvent = events[events.length - 1];
    const elapsedMs = Date.now() - new Date(lastEvent.at).getTime();
    if (elapsedMs > STALE_THRESHOLD_MS) return "failed";
    return "in_progress";
  }
  return undefined;
}

function deriveSteps(item: ActivityNotificationItem): readonly ActivityStep[] | undefined {
  if (item.edge == null) {
    if (CONFIGURATION_KINDS.has(item.kind)) {
      return [
        { label: "Saved", timestamp: item.createdAt, status: "completed" },
        {
          label: "Waiting for full config (routine, response & timezone required)",
          status: "pending",
        },
      ];
    }
    return undefined;
  }

  const { events, status } = item.edge;
  if (events.length === 0) return undefined;

  const lastEvent = events[events.length - 1];
  const elapsedMs = Date.now() - new Date(lastEvent.at).getTime();
  const stale = status === "pending" && elapsedMs > STALE_THRESHOLD_MS;

  const steps: ActivityStep[] = [];

  for (let i = 0; i < events.length; i++) {
    const event = events[i];
    const isLast = i === events.length - 1;
    let stepStatus: ActivityStepStatus;

    if (event.state === "failed") {
      stepStatus = "failed";
    } else if (!isLast) {
      stepStatus = "completed";
    } else {
      stepStatus =
        status === "successful" ? "completed"
        : status === "failed" || stale ? "failed"
        : "in_progress";
    }

    steps.push({
      label: edgeStateLabel(event.state),
      timestamp: event.at,
      status: stepStatus,
    });
  }

  if (status === "pending") {
    const lastState = lastEvent.state;

    if (stale) {
      steps.push({ label: "Timed out — camera did not respond", status: "failed" });
    } else if (lastState === "uploading") {
      steps.push({ label: "Applying to camera", status: "pending" });
      steps.push({ label: "Applied successfully", status: "pending" });
    } else if (lastState === "applying") {
      steps.push({ label: "Applied successfully", status: "pending" });
    }
  }

  return steps;
}

interface UseNotificationPanelOptions {
  enabled?: boolean;
}

export function useNotificationPanel({
  enabled = false,
}: UseNotificationPanelOptions = {}): UseNotificationPanelResult {
  const router = useRouter();
  const { user } = useUser();
  const tenantId = useTenantId();
  const { data: cameras } = useCameras();
  const {
    items: notifications,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useActivityNotifications({
    kinds: NOTIFICATION_KINDS,
    enabled,
  });
  const [visible, setVisible] = useState(false);
  const [lastSeenDate, setLastSeenDate] = useState<string | null>(null);

  const cameraNicknameMap = useMemo(() => {
    const map = new Map<string, string | undefined>();
    if (cameras) {
      for (const camera of cameras) {
        map.set(`${camera.key.asset}@${camera.key.id}`, camera.nickname ?? camera.name);
      }
    }
    return map;
  }, [cameras]);

  const storageKey = user?.id && tenantId ? `${LAST_SEEN_PREFIX}:${user.id}:${tenantId}` : null;

  useEffect(() => {
    if (!storageKey) return;
    AsyncStorage.getItem(storageKey).then((value: string | null) => {
      setLastSeenDate(value);
    });
  }, [storageKey]);

  const hasUnread = !visible && hasUnreadNotifications(notifications, lastSeenDate);

  const persistLastSeen = useCallback(
    (items: readonly ActivityNotificationItem[]) => {
      if (items.length > 0 && storageKey) {
        const latestDate = items[0].createdAt;
        setLastSeenDate(latestDate);
        AsyncStorage.setItem(storageKey, latestDate);
      }
    },
    [storageKey],
  );

  const open = useCallback(() => {
    setVisible(true);
  }, []);

  const close = useCallback(() => {
    setVisible(false);
    persistLastSeen(notifications);
  }, [notifications, persistLastSeen]);

  const markAllRead = useCallback(() => {
    persistLastSeen(notifications);
  }, [notifications, persistLastSeen]);

  const loadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const rawById = useMemo(() => {
    const map = new Map<string, ActivityNotificationItem>();
    for (const item of notifications) {
      map.set(item.id, item);
    }
    return map;
  }, [notifications]);

  const activities: ActivityItem[] = useMemo(
    () =>
      notifications.map((item) => ({
        id: item.id,
        kind: item.kind,
        summary: item.summary,
        status: deriveStatus(item),
        createdAt: item.createdAt,
        read: isNotificationRead(item, lastSeenDate),
        subtitle:
          item.siteId && item.cameraKey ?
            resolveCameraDisplayName(item.cameraKey, cameraNicknameMap.get(item.cameraKey))
          : undefined,
        steps: deriveSteps(item),
      })),
    [notifications, lastSeenDate, cameraNicknameMap],
  );

  const navigate = useCallback(
    (activity: ActivityItem) => {
      const raw = rawById.get(activity.id);
      if (raw?.siteId && raw.cameraKey && tenantId) {
        persistLastSeen(notifications);
        setVisible(false);
        router.push(`/tenants/${tenantId}/sites/${raw.siteId}/cameras/${raw.cameraKey}`);
      }
    },
    [rawById, tenantId, router, notifications, persistLastSeen],
  );

  return {
    visible,
    hasUnread,
    activities,
    hasMore: !!hasNextPage,
    isLoadingMore: isFetchingNextPage,
    onOpen: open,
    onClose: close,
    onMarkAllRead: markAllRead,
    onNavigate: navigate,
    onLoadMore: loadMore,
  };
}
